import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, ExternalLink } from 'lucide-react';

/*
 * Hotspot positions are calibrated per image.
 * Each Maxton Design project image is a 3/4 front elevated shot.
 * Coordinates are % from top-left of the image container.
 * 
 * Positions target the actual visible parts:
 * - Front lip/splitter: the black extension at the very front bottom of the bumper
 * - Side skirts: the extensions along the lower rocker panel
 * - Canards/flaps: small aerodynamic fins on front bumper corners
 * - Spoiler: rear wing or trunk lip
 * - Mirror caps: replacement covers on side mirrors
 */

const CARS = [
  {
    name: 'Audi RS3 8Y',
    front: '/images/cars/audi-rs3-8y.webp',
    hotspots: [
      { id: 'rs3-lip', x: 37, y: 75, label: 'Front Ansatz V.2', desc: 'Maxton Design Frontansatz für Audi RS3 8Y Limousine. Aggressiver Look, perfekte Passform.', price: 'ab 179 EUR', link: '/shop?q=front+ansatz+audi+rs3+8y' },
      { id: 'rs3-side', x: 74, y: 73, label: 'Seitenschweller Ansatz', desc: 'Maxton Design Seitenschweller-Ansätze für Audi RS3 8Y. Optische Tieferlegung.', price: 'ab 199 EUR', link: '/shop?q=seitenschweller+audi+rs3' },
      { id: 'rs3-spoiler', x: 70, y: 22, label: 'Heckspoiler', desc: 'Maxton Design Heckspoiler für Audi RS3 8Y. Sportlicher Abtrieb.', price: 'ab 139 EUR', link: '/shop?q=spoiler+audi+rs3' },
    ],
  },
  {
    name: 'BMW M2 G87',
    front: '/images/cars/bmw-m2-g87.png',
    hotspots: [
      { id: 'm2-lip', x: 38, y: 76, label: 'Carbon Frontlippe', desc: 'Maxton Design Carbon Frontspoiler für BMW M2 G87. Echtes Carbon, ultraleicht.', price: 'ab 349 EUR', link: '/shop?q=front+bmw+m2+g87' },
      { id: 'm2-canards', x: 62, y: 68, label: 'Racing Canards', desc: 'Maxton Design Carbon Canards für die Front. Racing-Look mit echtem Abtrieb.', price: 'ab 89 EUR', link: '/shop?q=canards+bmw+m2' },
      { id: 'm2-side', x: 82, y: 42, label: 'Seitenschweller', desc: 'Seitenschweller-Ansätze in Carbon für den BMW M2 G87.', price: 'ab 279 EUR', link: '/shop?q=seitenschweller+bmw+m2' },
    ],
  },
  {
    name: 'BMW M3 G80',
    front: '/images/cars/bmw-m3-g80.webp',
    hotspots: [
      { id: 'm3-lip', x: 38, y: 75, label: 'Front Ansatz Carbon', desc: 'Maxton Design Carbon Frontansatz für BMW M3/M4 G80/G82.', price: 'ab 389 EUR', link: '/shop?q=front+ansatz+bmw+m3+g80' },
      { id: 'm3-side', x: 75, y: 73, label: 'Seitenschweller Carbon', desc: 'Carbon Seitenschweller-Ansätze für den BMW M3 G80.', price: 'ab 299 EUR', link: '/shop?q=seitenschweller+bmw+m3+g80' },
      { id: 'm3-mirror', x: 60, y: 25, label: 'Spiegelkappen Carbon', desc: 'Carbon Spiegelkappen als direkter Ersatz.', price: 'ab 169 EUR', link: '/shop?q=spiegelkappen+carbon+bmw+m3' },
    ],
  },
  {
    name: 'Mercedes AMG GT',
    front: '/images/cars/mercedes-amg-gt.png',
    hotspots: [
      { id: 'amggt-lip', x: 37, y: 77, label: 'Front Ansatz', desc: 'Maxton Design Frontansatz für Mercedes-AMG GT. Motorsport-Optik.', price: 'ab 269 EUR', link: '/shop?q=front+ansatz+mercedes+amg+gt' },
      { id: 'amggt-canard', x: 25, y: 65, label: 'Racing Flaps', desc: 'Racing Flaps für die Frontschürze. Aerodynamischer Abtrieb.', price: 'ab 89 EUR', link: '/shop?q=flaps+mercedes+amg+gt' },
      { id: 'amggt-side', x: 74, y: 75, label: 'Seitenschweller', desc: 'Seitenschweller-Ansätze für den Mercedes-AMG GT.', price: 'ab 249 EUR', link: '/shop?q=seitenschweller+mercedes+amg+gt' },
      { id: 'amggt-hood', x: 50, y: 28, label: 'Motorhaube Aufsatz', desc: 'Carbon Motorhauben-Aufsatz für aggressiven Look.', price: 'ab 349 EUR', link: '/shop?q=motorhaube+mercedes+amg+gt' },
    ],
  },
  {
    name: 'BMW 7er G11',
    front: '/images/cars/bmw-7er-g11.png',
    hotspots: [
      { id: 'g11-lip', x: 37, y: 77, label: 'Front Ansatz', desc: 'Maxton Design Frontansatz für BMW 7er G11. Eleganter Sportstyle.', price: 'ab 219 EUR', link: '/shop?q=front+ansatz+bmw+7er' },
      { id: 'g11-side', x: 74, y: 75, label: 'Seitenschweller Ansätze', desc: 'Seitenschweller-Ansätze für den BMW 7er G11.', price: 'ab 229 EUR', link: '/shop?q=seitenschweller+bmw+7er' },
    ],
  },
  {
    name: 'BMW X5 G05',
    front: '/images/cars/bmw-x5-g05.png',
    hotspots: [
      { id: 'x5-lip', x: 37, y: 77, label: 'Front Ansatz', desc: 'Maxton Design Frontansatz für BMW X5 G05 M-Paket.', price: 'ab 199 EUR', link: '/shop?q=front+ansatz+bmw+x5+g05' },
      { id: 'x5-side', x: 74, y: 75, label: 'Seitenschweller', desc: 'Seitenschweller-Ansätze für den BMW X5 G05.', price: 'ab 229 EUR', link: '/shop?q=seitenschweller+bmw+x5' },
      { id: 'x5-grille', x: 50, y: 42, label: 'Kühlergrill Rahmen', desc: 'Kühlergrill-Zierleisten für den M-Look.', price: 'ab 89 EUR', link: '/shop?q=kuehlergrill+bmw+x5' },
    ],
  },
  {
    name: 'Corvette Z06',
    front: '/images/cars/corvette-z06.png',
    hotspots: [
      { id: 'z06-lip', x: 45, y: 76, label: 'Front Ansatz', desc: 'Maxton Design Frontansatz für Chevrolet Corvette Z06.', price: 'ab 289 EUR', link: '/shop?q=front+ansatz+corvette' },
      { id: 'z06-side', x: 85, y: 48, label: 'Seitenschweller', desc: 'Seitenschweller-Ansätze für die Corvette Z06.', price: 'ab 269 EUR', link: '/shop?q=seitenschweller+corvette' },
      { id: 'z06-splitter', x: 25, y: 68, label: 'Canards', desc: 'Racing Canards für die Frontschürze.', price: 'ab 99 EUR', link: '/shop?q=canards+corvette' },
    ],
  },
  {
    name: 'Mercedes A45 S',
    front: '/images/cars/mercedes-a45-s.png',
    hotspots: [
      { id: 'a45-lip', x: 37, y: 77, label: 'Front Ansatz', desc: 'Maxton Design Frontansatz für Mercedes-AMG A45 S W177.', price: 'ab 199 EUR', link: '/shop?q=front+ansatz+mercedes+a45' },
      { id: 'a45-side', x: 74, y: 75, label: 'Seitenschweller Ansätze', desc: 'Maxton Design Seitenschweller-Ansätze für Mercedes-AMG A45 S.', price: 'ab 219 EUR', link: '/shop?q=seitenschweller+mercedes+a45' },
      { id: 'a45-canard', x: 28, y: 70, label: 'Racing Flaps', desc: 'Racing Flaps für die Frontschürze.', price: 'ab 79 EUR', link: '/shop?q=flaps+mercedes+a45' },
    ],
  },
  {
    name: 'Ford Focus ST',
    front: '/images/cars/ford-focus-st.png',
    hotspots: [
      { id: 'focus-lip', x: 37, y: 77, label: 'Front Ansatz V.3', desc: 'Maxton Design Frontansatz V.3 für Ford Focus ST MK4.', price: 'ab 159 EUR', link: '/shop?q=front+ansatz+ford+focus+st' },
      { id: 'focus-side', x: 74, y: 75, label: 'Seitenschweller', desc: 'Seitenschweller-Ansätze für den Ford Focus ST MK4.', price: 'ab 179 EUR', link: '/shop?q=seitenschweller+ford+focus+st' },
      { id: 'focus-canard', x: 28, y: 70, label: 'Racing Canards', desc: 'Canards V.2 für die Frontschürze des Ford Focus ST.', price: 'ab 69 EUR', link: '/shop?q=canards+ford+focus+st' },
    ],
  },
  {
    name: 'BMW 3er G20',
    front: '/images/cars/bmw-3er-g20.png',
    hotspots: [
      { id: 'g20-lip', x: 37, y: 77, label: 'Front Ansatz', desc: 'Maxton Design Frontansatz für BMW 3er G20 M-Paket.', price: 'ab 169 EUR', link: '/shop?q=front+ansatz+bmw+3er+g20' },
      { id: 'g20-side', x: 74, y: 75, label: 'Seitenschweller', desc: 'Seitenschweller-Ansätze für den BMW 3er G20.', price: 'ab 189 EUR', link: '/shop?q=seitenschweller+bmw+3er+g20' },
      { id: 'g20-mirror', x: 65, y: 28, label: 'Spiegelkappen', desc: 'Spiegelkappen im M-Style für den BMW 3er G20.', price: 'ab 89 EUR', link: '/shop?q=spiegelkappen+bmw+3er+g20' },
    ],
  },
];

const HotspotShowcase = () => {
  const [carIdx, setCarIdx] = useState(0);
  const [active, setActive] = useState(null);
  const car = CARS[carIdx];

  return (
    <section id="hotspot" className="section-lg" data-testid="hotspot-section" style={{ background: 'var(--bg-surface)' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1.5rem' }}>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div className="caption" style={{ marginBottom: '1rem' }}>Interaktiv entdecken</div>
          <h2 className="heading-section" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}>
            Finde die perfekten <span style={{ color: 'var(--primary)' }}>Anbauteile</span>
          </h2>
          <p style={{ color: 'var(--fg-muted)', marginTop: '0.75rem', fontSize: '0.95rem' }}>
            Klicke auf die + Markierungen und entdecke passende Teile an unseren Beispielfahrzeugen. Unser Sortiment umfasst über 30.000 Teile an verschiedenen Spoilern, Diffusoren, Auspuffanlagen & mehr.
          </p>
        </motion.div>

        {/* Car Selector Tabs */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.35rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          {CARS.map((c, i) => (
            <button key={c.name} onClick={() => { setCarIdx(i); setActive(null); }}
              data-testid={`hotspot-car-${i}`}
              style={{
                padding: '0.55rem 1.1rem', fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.75rem',
                textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', border: 'none',
                background: i === carIdx ? 'var(--primary)' : 'rgba(255,255,255,0.05)',
                color: i === carIdx ? 'white' : 'var(--fg-muted)', transition: 'all 0.3s',
                transform: i === carIdx ? 'skewX(-5deg)' : 'none',
              }}>
              {c.name}
            </button>
          ))}
        </div>

        {/* Car Image with Hotspots */}
        <motion.div key={carIdx} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          style={{
            position: 'relative', maxWidth: '1100px', margin: '0 auto',
            background: 'linear-gradient(135deg, #0a0a0a 0%, #151515 50%, #0a0a0a 100%)',
            border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden',
          }}>
          <img src={car.front} alt={car.name}
            style={{ width: '100%', height: 'auto', display: 'block', minHeight: '300px', objectFit: 'contain', filter: 'contrast(1.05) saturate(1.05)' }}
            onError={e => e.target.style.opacity = '0.5'}
          />

          {/* Hotspot markers */}
          {car.hotspots.map(spot => (
            <button key={spot.id} data-testid={`hotspot-${spot.id}`}
              onClick={() => setActive(active?.id === spot.id ? null : spot)}
              style={{
                position: 'absolute', left: `${spot.x}%`, top: `${spot.y}%`,
                transform: 'translate(-50%, -50%)', width: '32px', height: '32px', borderRadius: '50%',
                background: active?.id === spot.id ? 'white' : 'var(--primary)',
                color: active?.id === spot.id ? 'var(--primary)' : 'white',
                border: '2px solid rgba(255,255,255,0.5)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
                transition: 'all 0.3s',
                boxShadow: '0 0 16px rgba(255,46,46,0.4), 0 0 32px rgba(255,46,46,0.15)',
                animation: active?.id === spot.id ? 'none' : 'hotspot-pulse 2s ease-in-out infinite',
              }}>
              {active?.id === spot.id ? <X size={12} strokeWidth={3} /> : <Plus size={14} strokeWidth={3} />}
            </button>
          ))}

          {/* Tooltip */}
          <AnimatePresence>
            {active && (
              <motion.div key={active.id} initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="glass" data-testid={`hotspot-tooltip-${active.id}`}
                style={{
                  position: 'absolute',
                  left: active.x < 50 ? `${Math.min(active.x + 3, 60)}%` : undefined,
                  right: active.x >= 50 ? `${Math.min(100 - active.x + 3, 60)}%` : undefined,
                  top: active.y < 50 ? `${active.y + 5}%` : undefined,
                  bottom: active.y >= 50 ? `${100 - active.y + 5}%` : undefined,
                  padding: '1.25rem', minWidth: '260px', maxWidth: '320px', zIndex: 20,
                }}>
                <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1rem', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
                  {active.label}
                </div>
                <p style={{ fontSize: '0.82rem', color: 'var(--fg-muted)', lineHeight: 1.5, marginBottom: '0.75rem' }}>{active.desc}</p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--primary)', fontWeight: 700, fontFamily: 'var(--font-heading)', fontSize: '1.1rem' }}>{active.price}</span>
                  <a href={active.link} target="_blank" rel="noopener noreferrer"
                    className="btn-primary" style={{ padding: '0.4rem 0.8rem', fontSize: '0.7rem', display: 'inline-flex' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>Im Shop <ExternalLink size={11} /></span>
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default HotspotShowcase;
